import type { IMenu } from '@/interfaces/IMenu';

/**
 * The multi-menu switcher's rows (plan §5.16): the base menu first, then
 * the store's live menus, each with the order types that use it (settings
 * `menu1Id` / `menu2Id` / `menu3Id`; unset = the base menu).
 */
export type TMenuOrderType = 'seat' | 'away' | 'delivery';

export interface IMenuUsageSettings {
  menu1Id?: number | null;
  menu2Id?: number | null;
  menu3Id?: number | null;
}

export interface IMenuChoice {
  /** null = the base menu. */
  id: number | null;
  /** null = the base menu (shown as «Menú principal»). */
  name: string | null;
  orderTypes: TMenuOrderType[];
}

const ORDER_TYPES: readonly TMenuOrderType[] = ['seat', 'away', 'delivery'];

function menuOfType(
  settings: IMenuUsageSettings | null,
  type: TMenuOrderType
): number | null {
  if (!settings) return null;
  if (type === 'seat') return settings.menu1Id ?? null;
  if (type === 'away') return settings.menu2Id ?? null;
  return settings.menu3Id ?? null;
}

export function menuChoices(
  menus: readonly IMenu[],
  settings: IMenuUsageSettings | null
): IMenuChoice[] {
  const typesOf = (id: number | null) =>
    ORDER_TYPES.filter((type) => menuOfType(settings, type) === id);
  return [
    { id: null, name: null, orderTypes: typesOf(null) },
    ...menus.map((menu) => ({
      id: menu.id,
      name: menu.name?.trim() || null,
      orderTypes: typesOf(menu.id),
    })),
  ];
}

/** How a menu is named on screen. */
export type TMenuChoiceName =
  | { kind: 'main' }
  | { kind: 'named'; name: string }
  | { kind: 'unnamed'; position: number };

/** «Menú principal», the menu's name, or «Menú {n}» by its position. */
export function menuChoiceName(
  choices: readonly IMenuChoice[],
  id: number | null
): TMenuChoiceName {
  if (id === null) return { kind: 'main' };
  const position = choices.findIndex((choice) => choice.id === id);
  const choice = choices[position];
  if (!choice) return { kind: 'main' };
  return choice.name
    ? { kind: 'named', name: choice.name }
    : { kind: 'unnamed', position };
}
